import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TablePagination,
  Stack,
  Typography
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import PasswordResetDialog from './PasswordResetDialog';
import { useIsAdmin, useHasAnyRole, usePermissions } from '../../contexts/PermissionsContext';

// Format a date string as a relative time (e.g., "2 hours ago", "3 days ago")
const formatRelativeDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

// Color mapping for role types - can be customized based on role properties
const getRoleColor = (roleName) => {
  const colorMap = {
    'tenant-level-admin': 'error',
    'league-manager': 'warning', 
    'team-manager': 'info',
    'spares-coordinator': 'secondary',
    'reporter': 'primary',
    'player': 'success',
    'viewer-only': 'default'
  };
  return colorMap[roleName] || 'default';
};

const OrganizationMembersTable = ({ viewFilter, rosterFilter, statusFilter, teamFilter, leagueFilter, canEdit, refreshKey }) => {
  const isAdmin = useIsAdmin();
  const isLeagueManager = useHasAnyRole(['league-manager']);
  const canDeleteUsers = useIsAdmin(); // Only Super Admins and Tenant Admins can delete users
  const { permissions } = usePermissions();

  const [members, setMembers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]); // Add roles state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('firstName');
  const [order, setOrder] = useState('asc');
  const [editingMember, setEditingMember] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Invitation state
  const [sendingInvitation, setSendingInvitation] = useState(null); // userTenantId being processed
  const [invitationMessage, setInvitationMessage] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null); // userTenantId for which link was copied
  
  // Password reset state
  const [passwordResetMember, setPasswordResetMember] = useState(null);

  // State for controlling dropdown open/close
  const [rolesDropdownOpen, setRolesDropdownOpen] = useState(false);
  const [teamsDropdownOpen, setTeamsDropdownOpen] = useState(false);
  const [leaguesDropdownOpen, setLeaguesDropdownOpen] = useState(false);
  const [playerTeamsDropdownOpen, setPlayerTeamsDropdownOpen] = useState(false);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [membersData, leaguesData, teamsData, rolesData] = await Promise.all([
          unifiedApi.getOrganizationMembers(),
          unifiedApi.get('/api/leagues'),
          unifiedApi.get('/api/teams'),
          unifiedApi.get('/api/Role')
        ]);

        if (membersData) {
          setMembers(membersData);
        } else {
          throw new Error('Failed to fetch members');
        }

        if (leaguesData) {
          setLeagues(leaguesData);
        }

        if (teamsData) {
          setTeams(teamsData);
        }

        if (rolesData) {
          // super-admin is Auth0-only; draft-viewer is managed per league from the League Admin Dashboard
          const filteredRoles = rolesData.filter(role => role.name !== 'super-admin' && role.name !== 'draft-viewer');
          setRoles(filteredRoles);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  // Helper function to get role info from the roles array
  const getRoleInfo = (roleName) => {
    const role = roles.find(r => r.name === roleName);
    return {
      name: roleName,
      label: role?.displayName || roleName,
      description: role?.description || '',
      color: getRoleColor(roleName)
    };
  };

  // Get all roles for a member (tenant + team + league)
  const getAllMemberRoles = (member) => {
    const allRoles = [];
    
    // Add tenant-level roles (excluding super-admin as it's Auth0-only)
    if (member.roles) {
      allRoles.push(...member.roles
        .filter(role => role.name !== 'super-admin')
        .map(role => ({
          name: role.name,
          displayName: role.displayName,
          type: 'tenant'
        })));
    }
    
    // Add team roles (including management roles)
    if (member.teamAssignments) {
      member.teamAssignments.forEach(team => {
        if (team.roleName && team.roleName !== 'player') {
          // Only show management roles in the roles column, not regular player roles
          allRoles.push({
            name: team.roleName,
            displayName: team.roleDisplayName,
            type: 'team',
            teamName: team.teamName
          });
        }
      });
    }
    
    // Add league roles
    if (member.leagueAssignments) {
      member.leagueAssignments.forEach(league => {
        allRoles.push({
          name: league.roleName,
          displayName: league.roleDisplayName,
          type: 'league',
          leagueName: league.leagueName
        });
      });
    }
    
    // Deduplicate roles by name - if someone has both tenant-level and team-specific
    // versions of the same role, only show it once (prefer tenant-level as it's broader)
    const uniqueRoles = [];
    const seenRoleNames = new Set();
    
    // First add tenant-level roles (higher priority)
    allRoles.filter(role => role.type === 'tenant').forEach(role => {
      if (!seenRoleNames.has(role.name)) {
        uniqueRoles.push(role);
        seenRoleNames.add(role.name);
      }
    });
    
    // Then add team and league roles that haven't been seen
    allRoles.filter(role => role.type !== 'tenant').forEach(role => {
      if (!seenRoleNames.has(role.name)) {
        uniqueRoles.push(role);
        seenRoleNames.add(role.name);
      }
    });
    
    return uniqueRoles;
  };

  // Check if a user can be deleted (no roles and no team assignments)
  const canDeleteMember = (member) => {
    const allRoles = getAllMemberRoles(member);
    const hasTeamAssignments = member.teamAssignments && member.teamAssignments.length > 0;
    
    // User can be deleted if they have no roles and no team assignments
    return allRoles.length === 0 && !hasTeamAssignments;
  };

  // Get member's invitation status for filtering
  const getMemberStatus = (member) => {
    if (member.hasAuth0Account || member.invitationStatus === 'Accepted') {
      return 'active';
    }
    if (member.invitationStatus === 'Pending') {
      return 'pending';
    }
    return 'not-invited';
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter members based on search and view filter
  const filteredMembers = useMemo(() => {
    // Comparator function for sorting
    const comparator = (a, b) => {
      let aValue, bValue;
      
      switch (orderBy) {
        case 'firstName':
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'status':
          aValue = getMemberStatus(a);
          bValue = getMemberStatus(b);
          break;
        case 'lastInvited':
          aValue = a.invitationSentAt || '';
          bValue = b.invitationSentAt || '';
          break;
        case 'lastLogin':
          aValue = a.lastLoginAt || '';
          bValue = b.lastLoginAt || '';
          break;
        case 'roles':
          aValue = getAllMemberRoles(a).length;
          bValue = getAllMemberRoles(b).length;
          break;
        case 'teams':
          aValue = (a.teamAssignments || []).length;
          bValue = (b.teamAssignments || []).length;
          break;
        default:
          aValue = a[orderBy] || '';
          bValue = b[orderBy] || '';
      }
      
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    };

    let result = members.filter(member => {
      const matchesSearch = searchTerm === '' || 
        member.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;

      // Apply roster filter
      if (rosterFilter !== 'both') {
        const isRostered = member.teamAssignments?.length > 0;
        if (rosterFilter === 'rostered' && !isRostered) return false;
        if (rosterFilter === 'unrostered' && isRostered) return false;
      }

      // Apply status filter
      if (statusFilter && statusFilter !== 'all') {
        const memberStatus = getMemberStatus(member);
        if (memberStatus !== statusFilter) return false;
      }

      // Apply league filter - show only members on teams in this league
      if (leagueFilter && leagueFilter !== 'all') {
        const leagueTeamIds = teams
          .filter(t => t.leagueId === parseInt(leagueFilter))
          .map(t => t.id);
        const memberTeamIds = (member.teamAssignments || []).map(t => t.teamId);
        const isInLeague = memberTeamIds.some(id => leagueTeamIds.includes(id));
        const hasLeagueRole = (member.leagueAssignments || []).some(
          l => l.leagueId === parseInt(leagueFilter)
        );
        if (!isInLeague && !hasLeagueRole) return false;
      }

      // Apply team filter (only for active season teams)
      if (teamFilter && teamFilter !== 'all') {
        const memberTeamIds = (member.teamAssignments || []).map(t => t.teamId);
        if (!memberTeamIds.includes(parseInt(teamFilter))) return false;
      }

      // Get all roles for filtering
      const allRoles = getAllMemberRoles(member);
      const roleNames = allRoles.map(r => r.name);

      switch (viewFilter) {
        case 'elevated':
          return roleNames.some(roleName => 
            ['tenant-level-admin', 'league-manager', 'team-manager', 'spares-coordinator', 'reporter'].includes(roleName)
          );
        case 'admins':
          return roleNames.some(roleName => 
            ['tenant-level-admin'].includes(roleName)
          );
        case 'managers':
          return roleNames.some(roleName => 
            ['league-manager', 'team-manager'].includes(roleName)
          );
        case 'players':
          // Players are those who have team assignments but no management roles
          return member.teamAssignments?.length > 0 && 
                 !roleNames.some(roleName => ['tenant-level-admin', 'league-manager', 'team-manager'].includes(roleName));
        default:
          return true;
      }
    });

    // Apply sorting
    return result.sort(comparator);
  }, [members, teams, searchTerm, rosterFilter, statusFilter, teamFilter, leagueFilter, viewFilter, order, orderBy]);

  // Get roles that current user can assign
  const getAssignableRoles = () => {
    if (isAdmin) {
      // Return all available roles (super-admin already filtered out)
      return roles.map(role => role.name);
    } else if (isLeagueManager) {
      // League managers can assign these specific roles
      const allowedRoles = ['league-manager', 'reporter', 'viewer-only', 'spares-coordinator', 'team-manager', 'player'];
      return roles.filter(role => allowedRoles.includes(role.name)).map(role => role.name);
    }
    return [];
  };

  const handleEditMember = (member) => {
    // Get all current roles (tenant, team, league) for proper initialization
    const allCurrentRoles = getAllMemberRoles(member);
    const allCurrentRoleNames = allCurrentRoles.map(r => r.name);
    
    setEditingMember({
      ...member,
      selectedRoles: allCurrentRoleNames,
      selectedTeams: member.teamAssignments?.map(t => t.teamId) || [],
      selectedLeagues: member.leagueAssignments?.map(l => l.leagueId) || [],
      selectedPlayerTeams: member.teamAssignments?.map(t => t.teamId) || []
    });
    setEditModalOpen(true);
  };

  const handleSaveRoles = async () => {
    if (!editingMember) return;

    setSaving(true);
    try {
      // Get current roles to compare
      const currentRoles = editingMember.roles?.map(r => r.name) || [];
      const selectedRoles = editingMember.selectedRoles || [];
      const currentTeams = editingMember.teamAssignments?.map(t => t.teamId) || [];
      const selectedTeams = editingMember.selectedTeams || [];
      const currentLeagues = editingMember.leagueAssignments?.map(l => l.leagueId) || [];
      const selectedLeagues = editingMember.selectedLeagues || [];

      // We need the UserTenantId for role assignments
      // editingMember.id is now the UserTenantId from the updated members API
      const userTenantId = editingMember.id;

      if (!userTenantId) {
        throw new Error('Missing UserTenantId for role assignment');
      }

      if (!permissions?.tenantId) {
        throw new Error('Permissions not loaded — cannot determine tenant for role assignment');
      }

      // Get all current roles for comparison
      const allCurrentRoles = getAllMemberRoles(editingMember);
      const allCurrentRoleNames = allCurrentRoles.map(r => r.name);
      
      // Handle role changes based on what's been selected/deselected
      const rolesToAddGlobal = selectedRoles.filter(role => !allCurrentRoleNames.includes(role));
      const rolesToRemoveGlobal = allCurrentRoleNames.filter(role => !selectedRoles.includes(role));
      
      console.log('All current roles:', allCurrentRoleNames);
      console.log('Selected roles:', selectedRoles);
      console.log('Roles to add:', rolesToAddGlobal);
      console.log('Roles to remove:', rolesToRemoveGlobal);
      
      // Handle tenant-level role changes
      // Process all roles that can be tenant-level, including team-manager and league-manager
      // These can exist as both tenant-level (broad permissions) and specific assignments
      const tenantLevelRoles = ['tenant-level-admin', 'viewer-only', 'spares-coordinator', 'reporter', 'player', 'team-manager', 'league-manager'];
      const selectedTenantRoles = selectedRoles.filter(role => tenantLevelRoles.includes(role));
      const currentTenantRoles = currentRoles.filter(role => tenantLevelRoles.includes(role));
      
      const rolesToAdd = selectedTenantRoles.filter(role => !currentTenantRoles.includes(role));
      const rolesToRemove = currentTenantRoles.filter(role => !selectedTenantRoles.includes(role));

      const rolePromises = [];

      // Assign new tenant roles
      for (const roleName of rolesToAdd) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/tenants/${permissions.tenantId}/roles`, {
            userTenantId: userTenantId,
            roleName: roleName,
            isAssigned: true
          }).then(() => ({ action: 'assign', role: roleName, success: true }))
        );
      }

      // Remove old tenant roles
      for (const roleName of rolesToRemove) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/tenants/${permissions.tenantId}/roles`, {
            userTenantId: userTenantId,
            roleName: roleName,
            isAssigned: false
          }).then(() => ({ action: 'remove', role: roleName, success: true }))
        );
      }

      // Handle team-manager role changes (based on role selection, not just team selection)
      const isRemovingTeamManagerRole = rolesToRemoveGlobal.includes('team-manager');
      const hasTeamManagerAsTenantRole = currentRoles.includes('team-manager');
      
      // Handle team-level role changes
      let teamsToAdd = selectedTeams.filter(teamId => !currentTeams.includes(teamId));
      let teamsToRemove = currentTeams.filter(teamId => !selectedTeams.includes(teamId));
      
      // If team-manager role is being removed globally AND user has it as tenant-level role,
      // also remove from all team-specific assignments
      if (isRemovingTeamManagerRole && !hasTeamManagerAsTenantRole && editingMember.teamAssignments?.some(t => t.roleName === 'team-manager')) {
        // Only remove from team-specific assignments if it's not a tenant-level role
        const allCurrentTeamIds = editingMember.teamAssignments?.filter(t => t.roleName === 'team-manager').map(t => t.teamId) || [];
        for (const teamId of allCurrentTeamIds) {
          if (!teamsToRemove.includes(teamId)) {
            teamsToRemove.push(teamId);
          }
        }
        console.log('Removing team-manager role from specific team assignments:', teamsToRemove);
      }

      // Assign team management roles
      for (const teamId of teamsToAdd) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/teams/${teamId}/roles`, {
            userTenantId: userTenantId,
            roleName: 'team-manager',
            isAssigned: true
          }).then(() => ({ action: 'assign', role: 'team-manager', teamId, success: true }))
        );
      }

      // Remove team management roles
      for (const teamId of teamsToRemove) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/teams/${teamId}/roles`, {
            userTenantId: userTenantId,
            roleName: 'team-manager',
            isAssigned: false
          }).then(() => ({ action: 'remove', role: 'team-manager', teamId, success: true }))
        );
      }

      // Handle league-manager role changes (based on role selection, not just league selection)  
      const isRemovingLeagueManagerRole = rolesToRemoveGlobal.includes('league-manager');
      const isRemovingReporterRole = rolesToRemoveGlobal.includes('reporter');
      const hasLeagueManagerAsTenantRole = currentRoles.includes('league-manager');
      
      // Handle league-level role changes
      let leaguesToAdd = selectedLeagues.filter(leagueId => !currentLeagues.includes(leagueId));
      let leaguesToRemove = currentLeagues.filter(leagueId => !selectedLeagues.includes(leagueId));
      const reporterLeaguesToRemove = isRemovingReporterRole
        ? editingMember.leagueAssignments?.filter(league => league.roleName === 'reporter').map(league => league.leagueId) || []
        : [];
      
      // If league-manager role is being removed globally AND user has it as tenant-level role,
      // also remove from all league-specific assignments
      if (isRemovingLeagueManagerRole && !hasLeagueManagerAsTenantRole && editingMember.leagueAssignments?.length > 0) {
        // Only remove from league-specific assignments if it's not a tenant-level role
        const allCurrentLeagueIds = editingMember.leagueAssignments?.map(l => l.leagueId) || [];
        for (const leagueId of allCurrentLeagueIds) {
          if (!leaguesToRemove.includes(leagueId)) {
            leaguesToRemove.push(leagueId);
          }
        }
        console.log('Removing league-manager role from specific league assignments:', leaguesToRemove);
      }

      // Assign league management roles
      for (const leagueId of leaguesToAdd) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/leagues/${leagueId}/roles`, {
            userTenantId: userTenantId,
            roleName: 'league-manager',
            isAssigned: true
          }).then(() => ({ action: 'assign', role: 'league-manager', leagueId, success: true }))
        );
      }

      // Remove league management roles
      for (const leagueId of leaguesToRemove) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/leagues/${leagueId}/roles`, {
            userTenantId: userTenantId,
            roleName: 'league-manager',
            isAssigned: false
          }).then(() => ({ action: 'remove', role: 'league-manager', leagueId, success: true }))
        );
      }

      // Remove league-scoped reporter roles when reporter is deselected globally
      for (const leagueId of reporterLeaguesToRemove) {
        rolePromises.push(
          unifiedApi.post(`/api/RoleManagement/leagues/${leagueId}/roles`, {
            userTenantId: userTenantId,
            roleName: 'reporter',
            isAssigned: false
          }).then(() => ({ action: 'remove', role: 'reporter', leagueId, success: true }))
        );
      }

      // Handle player team assignments (using role management API for consistency)
      const currentPlayerTeams = editingMember.teamAssignments?.map(t => t.teamId) || [];
      const currentHasPlayerRole = currentRoles.includes('player') || editingMember.teamAssignments?.some(t => t.roleName === 'player');
      const selectedHasPlayerRole = selectedRoles.includes('player');
      
      if (selectedHasPlayerRole) {
        // User has player role selected - handle team assignments using role management API
        const selectedPlayerTeams = editingMember.selectedPlayerTeams || [];
        const playerTeamsToAdd = selectedPlayerTeams.filter(teamId => !currentPlayerTeams.includes(teamId));
        const playerTeamsToRemove = currentPlayerTeams.filter(teamId => !selectedPlayerTeams.includes(teamId));

        // Add player role to teams
        for (const teamId of playerTeamsToAdd) {
          rolePromises.push(
            unifiedApi.post(`/api/RoleManagement/teams/${teamId}/roles`, {
              userTenantId: userTenantId,
              roleName: 'player',
              isAssigned: true
            }).then(() => ({ action: 'assign-player-role', teamId, success: true }))
          );
        }

        // Remove player role from teams
        for (const teamId of playerTeamsToRemove) {
          rolePromises.push(
            unifiedApi.post(`/api/RoleManagement/teams/${teamId}/roles`, {
              userTenantId: userTenantId,
              roleName: 'player',
              isAssigned: false
            }).then(() => ({ action: 'remove-player-role', teamId, success: true }))
          );
        }
      } else if (currentHasPlayerRole && !selectedHasPlayerRole) {
        // User had player role but it's being removed - remove player role from all teams
        // This will also remove them from teams entirely if they have no other roles (handled by backend)
        for (const teamId of currentPlayerTeams) {
          rolePromises.push(
            unifiedApi.post(`/api/RoleManagement/teams/${teamId}/roles`, {
              userTenantId: userTenantId,
              roleName: 'player',
              isAssigned: false
            }).then(() => ({ action: 'remove-player-role', teamId, success: true }))
          );
        }
      }

      // Wait for all role changes to complete
      await Promise.all(rolePromises);

      // Refresh members data
      const membersData = await unifiedApi.getOrganizationMembers();
      if (membersData) {
        setMembers(membersData);
      }
      
      setEditModalOpen(false);
      setEditingMember(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = (member) => {
    setMemberToDelete(member);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    setDeleting(true);
    try {
      await unifiedApi.delete(`/api/UserTenant/${memberToDelete.id}`);

      // Refresh members data
      const membersData = await unifiedApi.getOrganizationMembers();
      if (membersData) {
        setMembers(membersData);
      }

      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setMemberToDelete(null);
  };

  // Invitation handlers
  const handleSendInvitationEmail = async (member) => {
    setSendingInvitation(member.id);
    setInvitationMessage(null);
    try {
      const result = await unifiedApi.sendInvitationEmail(member.id);
      if (result?.success) {
        setInvitationMessage({ type: 'success', text: result.message });
        // Update member in state to reflect invitation sent
        setMembers(prev => prev.map(m => 
          m.id === member.id 
            ? { ...m, invitationSentAt: new Date().toISOString(), invitationMethod: 'Email', invitationStatus: 'Pending' }
            : m
        ));
      } else {
        setInvitationMessage({ type: 'error', text: result?.message || 'Failed to send invitation' });
      }
    } catch (err) {
      setInvitationMessage({ type: 'error', text: err.message || 'Failed to send invitation' });
    } finally {
      setSendingInvitation(null);
    }
  };

  const handleCopyInvitationLink = async (member) => {
    setSendingInvitation(member.id);
    setInvitationMessage(null);
    try {
      const result = await unifiedApi.generateInvitationLink(member.id);
      if (result?.invitationUrl) {
        await navigator.clipboard.writeText(result.invitationUrl);
        setCopiedLink(member.id);
        setInvitationMessage({ type: 'success', text: `Link copied for ${member.email}` });
        // Update member in state to reflect invitation generated
        setMembers(prev => prev.map(m => 
          m.id === member.id 
            ? { ...m, invitationSentAt: new Date().toISOString(), invitationMethod: 'LinkCopied', invitationStatus: 'Pending' }
            : m
        ));
        // Clear the "copied" indicator after 3 seconds
        setTimeout(() => setCopiedLink(null), 3000);
      } else {
        setInvitationMessage({ type: 'error', text: 'Failed to generate invitation link' });
      }
    } catch (err) {
      setInvitationMessage({ type: 'error', text: err.message || 'Failed to generate invitation link' });
    } finally {
      setSendingInvitation(null);
    }
  };

  const getInvitationStatusChip = (member) => {
    if (member.hasAuth0Account || member.invitationStatus === 'Accepted') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Active"
          size="small"
          color="success"
          variant="outlined"
          onClick={() => {/* do nothing */}}
        />
      );
    }
    if (member.invitationStatus === 'Pending') {
      return (
        <Tooltip title={`Invited via ${member.invitationMethod || 'unknown'} on ${member.invitationSentAt ? new Date(member.invitationSentAt).toLocaleDateString() : 'unknown date'}`}>
          <Chip
            icon={<ScheduleIcon />}
            label="Pending"
            size="small"
            color="warning"
            variant="outlined"
            onClick={() => {/* do nothing */}}
          />
        </Tooltip>
      );
    }
    return (
      <Chip
        icon={<HourglassEmptyIcon />}
        label="Not Invited"
        size="small"
        color="default"
        variant="outlined"
        onClick={() => {/* do nothing */}}
      />
    );
  };

  const renderRoleChips = (member) => {
    const allRoles = getAllMemberRoles(member);
    
    if (allRoles.length === 0) {
      return <Chip label="No roles" size="small" color="default" />;
    }
    
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {allRoles.map((role, index) => {
          const roleInfo = getRoleInfo(role.name);
          const tooltip = role.type === 'team' ? `Team: ${role.teamName}` : 
                         role.type === 'league' ? `League: ${role.leagueName}` : 
                         'Organization Role';
          
          return (
            <Tooltip key={index} title={tooltip}>
              <Chip
                label={roleInfo.label}
                size="small"
                color={roleInfo.color}
                variant="outlined"
              />
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const paginatedMembers = filteredMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search members..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>

      {/* Invitation message alert */}
      {invitationMessage && (
        <Alert 
          severity={invitationMessage.type} 
          onClose={() => setInvitationMessage(null)}
          sx={{ mb: 2 }}
        >
          {invitationMessage.text}
        </Alert>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'firstName' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'firstName'}
                    direction={orderBy === 'firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('firstName')}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'email' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'status' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'lastInvited' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'lastInvited'}
                    direction={orderBy === 'lastInvited' ? order : 'asc'}
                    onClick={() => handleRequestSort('lastInvited')}
                  >
                    Last Invited
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'lastLogin' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'lastLogin'}
                    direction={orderBy === 'lastLogin' ? order : 'asc'}
                    onClick={() => handleRequestSort('lastLogin')}
                  >
                    Last Login
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'roles' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'roles'}
                    direction={orderBy === 'roles' ? order : 'asc'}
                    onClick={() => handleRequestSort('roles')}
                  >
                    Roles
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'teams' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'teams'}
                    direction={orderBy === 'teams' ? order : 'asc'}
                    onClick={() => handleRequestSort('teams')}
                  >
                    Teams
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMembers.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" component={Link} to={`/player/${member.id}`} sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                          {member.firstName} {member.lastName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {member.email || 'No email'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getInvitationStatusChip(member)}
                  </TableCell>
                  <TableCell>
                    {!member.hasAuth0Account && member.invitationStatus !== 'Accepted' ? (
                      member.invitationSentAt ? (
                        <Typography variant="body2">
                          {new Date(member.invitationSentAt).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Never
                        </Typography>
                      )
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {member.lastLoginAt ? (
                      <Tooltip title={new Date(member.lastLoginAt).toLocaleString()}>
                        <Typography variant="body2">
                          {formatRelativeDate(member.lastLoginAt)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {renderRoleChips(member)}
                  </TableCell>
                  <TableCell>
                    {member.teamAssignments?.length > 0 ? (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {member.teamAssignments.map((team, index) => (
                          <Chip
                            key={index}
                            label={team.teamName}
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => {/* do nothing */}}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No teams
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {/* Show invitation buttons only for users without Auth0 accounts */}
                      {!member.hasAuth0Account && member.invitationStatus !== 'Accepted' && canEdit && (
                        <>
                          <Tooltip title="Send invitation email">
                            <IconButton
                              size="small"
                              onClick={() => handleSendInvitationEmail(member)}
                              disabled={sendingInvitation === member.id}
                              color="primary"
                            >
                              {sendingInvitation === member.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <EmailIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={copiedLink === member.id ? "Link copied!" : "Copy invitation link"}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyInvitationLink(member)}
                              disabled={sendingInvitation === member.id}
                              color={copiedLink === member.id ? "success" : "default"}
                            >
                              {copiedLink === member.id ? (
                                <CheckCircleIcon fontSize="small" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {member.hasAuth0Account && canEdit && (
                        <Tooltip title="Password reset">
                          <IconButton
                            size="small"
                            onClick={() => setPasswordResetMember(member)}
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit && (
                        <Tooltip title="Edit roles">
                          <IconButton
                            aria-label="Edit roles"
                            size="small"
                            onClick={() => handleEditMember(member)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteUsers && canDeleteMember(member) && (
                        <Tooltip title="Delete user">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMember(member)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedMembers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No members found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredMembers.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Edit Member Roles Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Member Roles - {editingMember?.firstName} {editingMember?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                open={rolesDropdownOpen}
                onClose={() => setRolesDropdownOpen(false)}
                onOpen={() => setRolesDropdownOpen(true)}
                inputProps={{ 'aria-label': 'Roles' }}
                value={editingMember?.selectedRoles || []}
                label="Roles"
                onChange={(e) => {
                  setEditingMember(prev => ({
                    ...prev,
                    selectedRoles: e.target.value
                  }));
                  // Close dropdown after selection
                  setRolesDropdownOpen(false);
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((roleName) => {
                      const roleInfo = getRoleInfo(roleName);
                      return (
                        <Chip
                          key={roleName}
                          label={roleInfo.label}
                          size="small"
                          color={roleInfo.color}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {getAssignableRoles().map((roleName) => {
                  const roleInfo = getRoleInfo(roleName);
                  return (
                    <MenuItem key={roleName} value={roleName}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {roleInfo.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {roleInfo.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {editingMember?.selectedRoles?.includes('team-manager') && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Manage Teams</InputLabel>
                <Select
                  multiple
                  open={teamsDropdownOpen}
                  onClose={() => setTeamsDropdownOpen(false)}
                  onOpen={() => setTeamsDropdownOpen(true)}
                  value={editingMember?.selectedTeams || []}
                  label="Manage Teams"
                  onChange={(e) => {
                    setEditingMember(prev => ({
                      ...prev,
                      selectedTeams: e.target.value
                    }));
                    // Close dropdown after selection
                    setTeamsDropdownOpen(false);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((teamId) => {
                        const team = teams.find(t => t.id === teamId);
                        return (
                          <Chip
                            key={teamId}
                            label={team?.name || `Team ${teamId}`}
                            size="small"
                            color="primary"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {editingMember?.selectedRoles?.includes('league-manager') && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Manage Leagues</InputLabel>
                <Select
                  multiple
                  open={leaguesDropdownOpen}
                  onClose={() => setLeaguesDropdownOpen(false)}
                  onOpen={() => setLeaguesDropdownOpen(true)}
                  value={editingMember?.selectedLeagues || []}
                  label="Manage Leagues"
                  onChange={(e) => {
                    setEditingMember(prev => ({
                      ...prev,
                      selectedLeagues: e.target.value
                    }));
                    // Close dropdown after selection
                    setLeaguesDropdownOpen(false);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((leagueId) => {
                        const league = leagues.find(l => l.id === leagueId);
                        return (
                          <Chip
                            key={leagueId}
                            label={league?.name || `League ${leagueId}`}
                            size="small"
                            color="warning"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {leagues.map((league) => (
                    <MenuItem key={league.id} value={league.id}>
                      {league.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {editingMember?.selectedRoles?.includes('player') && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Join Teams</InputLabel>
                <Select
                  multiple
                  open={playerTeamsDropdownOpen}
                  onClose={() => setPlayerTeamsDropdownOpen(false)}
                  onOpen={() => setPlayerTeamsDropdownOpen(true)}
                  value={editingMember?.selectedPlayerTeams || []}
                  label="Join Teams"
                  onChange={(e) => {
                    setEditingMember(prev => ({
                      ...prev,
                      selectedPlayerTeams: e.target.value
                    }));
                    // Close dropdown after selection
                    setPlayerTeamsDropdownOpen(false);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((teamId) => {
                        const team = teams.find(t => t.id === teamId);
                        return (
                          <Chip
                            key={teamId}
                            label={team?.name || `Team ${teamId}`}
                            size="small"
                            color="success"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditModalOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRoles}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        open={!!passwordResetMember}
        onClose={() => setPasswordResetMember(null)}
        member={passwordResetMember}
      />

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The user will be permanently deleted from the system.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete{' '}
            <strong>
              {memberToDelete?.firstName} {memberToDelete?.lastName}
            </strong>{' '}
            ({memberToDelete?.email})?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This user has no roles and no team assignments, so they can be safely deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationMembersTable;